import type { MembershipAccess, Session, Streamer } from "./api";

export type MembershipAccessStatus = MembershipAccess["status"];
export type SessionStatus = "Pending" | "Live" | "Ended" | "Failed";
export type StreamerStatus = "Pending" | "Ready" | "Revoked" | "Blocked";

function normalizeEnumToken(value: string): string {
    return value.trim().toLowerCase().replace(/_/g, "");
}

export function normalizeMembershipAccessStatus(status: string | undefined): MembershipAccessStatus | undefined {
    if (!status) {
        return undefined;
    }

    switch (normalizeEnumToken(status)) {
    case "available":
        return "Available";
    case "missingrequiredfeature":
        return "MissingRequiredFeature";
    case "verificationunavailable":
        return "VerificationUnavailable";
    default:
        return status as MembershipAccessStatus;
    }
}

export function normalizeSessionStatus(status: string): SessionStatus | string {
    switch (normalizeEnumToken(status)) {
    case "pending":
        return "Pending";
    case "live":
        return "Live";
    case "ended":
        return "Ended";
    case "failed":
        return "Failed";
    default:
        return status;
    }
}

export function normalizeStreamerStatus(status: string): StreamerStatus | string {
    switch (normalizeEnumToken(status)) {
    case "pending":
        return "Pending";
    case "ready":
        return "Ready";
    case "revoked":
        return "Revoked";
    case "blocked":
        return "Blocked";
    default:
        return status;
    }
}

export function isLiveSessionStatus(status: string): boolean {
    return normalizeEnumToken(status) === "live";
}

export function normalizeStreamer(streamer: Streamer): Streamer {
    return {
        ...streamer,
        status: normalizeStreamerStatus(streamer.status),
        membershipAccess: streamer.membershipAccess
            ? {
                ...streamer.membershipAccess,
                status: normalizeMembershipAccessStatus(streamer.membershipAccess.status) ?? streamer.membershipAccess.status,
            }
            : streamer.membershipAccess,
    };
}

export function normalizeSession(session: Session): Session {
    const status = normalizeSessionStatus(session.status);

    return {
        ...session,
        status,
        isLive: session.isLive || isLiveSessionStatus(status),
    };
}
